import { delay, iconClassName, styles, svgProps } from '@/components/service-showcase/icons/shared';
import type { IServiceShowcaseSvgIconProps } from '@/components/service-showcase/icons/type';

// Chat interface frame centred horizontally in the 380×220 viewBox
const FRAME = { x: 118, y: 10, w: 160, h: 200, rx: 13 };
const TOP_BAR_Y = FRAME.y + 38;

const BUBBLES = [
  { x: FRAME.x + 16, y: 60, w: 102, h: 22, rx: 11, glowing: false, d: 0.3 },
  { x: FRAME.x + 28, y: 96, w: 118, h: 22, rx: 11, glowing: true, d: 0.52 },
  { x: FRAME.x + 16, y: 132, w: 88, h: 22, rx: 11, glowing: false, d: 0.74 },
];

const TYPING_DOTS = [
  { cx: FRAME.x + 58, cy: 172, begin: '0s' },
  { cx: FRAME.x + 74, cy: 172, begin: '0.2s' },
  { cx: FRAME.x + 90, cy: 172, begin: '0.4s' },
];

export default function ProductSurfaceIcon(props: IServiceShowcaseSvgIconProps) {
  const { active = true, className, ...rest } = props;

  return (
    <svg {...svgProps} {...rest} className={iconClassName(active, className)}>
      {/* Device frame */}
      <rect
        x={FRAME.x} y={FRAME.y} width={FRAME.w} height={FRAME.h} rx={FRAME.rx}
        stroke='currentColor' strokeOpacity='0.6' strokeWidth='1.3' fill='none'
        className={styles.ring} style={delay('0s')}
      />

      {/* Top bar separator */}
      <line x1={FRAME.x} y1={TOP_BAR_Y} x2={FRAME.x + FRAME.w} y2={TOP_BAR_Y} stroke='currentColor' strokeOpacity='0.15' strokeWidth='1' />

      {/* Nav dots */}
      <circle cx={FRAME.x + 16} cy={FRAME.y + 19} r='3' fill='currentColor' fillOpacity='0.42' className={styles.pop} style={delay('0.12s')} />
      <circle cx={FRAME.x + 30} cy={FRAME.y + 19} r='3' fill='currentColor' fillOpacity='0.3' className={styles.pop} style={delay('0.16s')} />

      {/* Chat bubbles */}
      {BUBBLES.map(({ x, y, w, h, rx, glowing, d }) => (
        <rect
          key={`${x}-${y}`}
          x={x} y={y} width={w} height={h} rx={rx}
          stroke='currentColor' strokeOpacity={glowing ? 0.7 : 0.32} strokeWidth='1.2'
          fill='currentColor' fillOpacity={glowing ? 0.08 : 0.04}
          className={styles.pop} style={delay(`${d}s`)}
        />
      ))}

      {/* Typing indicator — SMIL opacity wave gated by .sweep */}
      <g className={styles.sweep}>
        {TYPING_DOTS.map(({ cx, cy, begin }) => (
          <circle key={cx} cx={cx} cy={cy} r='3.5' fill='currentColor'>
            <animate attributeName='opacity' values='0.3;0.9;0.3' dur='1.2s' begin={begin} repeatCount='indefinite' />
          </circle>
        ))}
      </g>

      {/* LIVE badge */}
      <text x={FRAME.x + FRAME.w - 32} y={FRAME.y + 22} fill='currentColor' fillOpacity='0.38' fontSize='8' fontFamily='monospace' letterSpacing='0.1em'>LIVE</text>
      <circle
        cx={FRAME.x + FRAME.w - 38} cy={FRAME.y + 18} r='3'
        fill='currentColor' fillOpacity='0.8'
        className={styles.radarBlip} style={delay('0.1s')}
      />
    </svg>
  );
}
